/**
 * Snakes and Ladders Game Persistence Service
 *
 * Auto-saves game state to local storage after each move.
 * Follows BattleshipGamePersistence pattern.
 */

import {
  SnakesLaddersGameState,
  SnakesLaddersGameSettings,
  SnakesLaddersDifficulty,
} from '../types';
import { LocalStorageManager } from '../../services/LocalStorageManager';
import { DataSynchronizationService } from '../../services/DataSynchronizationService';

export interface SavedSnakesLaddersGame {
  gameId: string;
  mode: SnakesLaddersGameSettings['mode'];
  gameState: SnakesLaddersGameState;
  metadata: {
    aiDifficulty?: SnakesLaddersDifficulty;
    startedAt: number;
    lastSavedAt: number;
  };
}

export class SnakesLaddersGamePersistence {
  private storage: LocalStorageManager;
  private syncService: DataSynchronizationService | null;
  private readonly STORAGE_KEY = 'snakes_ladders_saved_games';
  private readonly AUTO_SAVE_KEY = 'snakes_ladders_auto_save';

  constructor(
    storage: LocalStorageManager,
    syncService?: DataSynchronizationService
  ) {
    this.storage = storage;
    this.syncService = syncService || null;
  }

  /** Auto-save the current game state after each move. */
  autoSave(
    mode: SnakesLaddersGameSettings['mode'],
    gameState: SnakesLaddersGameState,
    metadata: SavedSnakesLaddersGame['metadata']
  ): boolean {
    try {
      const savedGame: SavedSnakesLaddersGame = {
        gameId: 'auto_save',
        mode,
        gameState,
        metadata: { ...metadata, lastSavedAt: Date.now() },
      };
      localStorage.setItem(this.AUTO_SAVE_KEY, JSON.stringify(savedGame));
      return true;
    } catch (error) {
      console.error('Failed to auto-save snakes & ladders game:', error);
      return false;
    }
  }

  /** Load the auto-saved game state. */
  loadAutoSave(): SavedSnakesLaddersGame | null {
    try {
      const data = localStorage.getItem(this.AUTO_SAVE_KEY);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error('Failed to load snakes & ladders auto-save:', error);
      return null;
    }
  }

  /** Clear the auto-saved game state. */
  clearAutoSave(): boolean {
    try {
      localStorage.removeItem(this.AUTO_SAVE_KEY);
      return true;
    } catch (error) {
      console.error('Failed to clear snakes & ladders auto-save:', error);
      return false;
    }
  }

  /** Check if an auto-save exists. */
  hasAutoSave(): boolean {
    return this.loadAutoSave() !== null;
  }

  /** Save a game with a specific ID. */
  saveGame(
    gameId: string,
    mode: SnakesLaddersGameSettings['mode'],
    gameState: SnakesLaddersGameState,
    metadata: SavedSnakesLaddersGame['metadata']
  ): boolean {
    try {
      const savedGame: SavedSnakesLaddersGame = {
        gameId,
        mode,
        gameState,
        metadata: { ...metadata, lastSavedAt: Date.now() },
      };

      const games = this.loadAllGames();
      const existingIndex = games.findIndex((g) => g.gameId === gameId);

      if (existingIndex >= 0) {
        games[existingIndex] = savedGame;
      } else {
        games.push(savedGame);
      }

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(games));
      return true;
    } catch (error) {
      console.error('Failed to save snakes & ladders game:', error);
      return false;
    }
  }

  /** Load a saved game by ID. */
  loadGame(gameId: string): SavedSnakesLaddersGame | null {
    try {
      const games = this.loadAllGames();
      return games.find((g) => g.gameId === gameId) || null;
    } catch (error) {
      console.error('Failed to load snakes & ladders game:', error);
      return null;
    }
  }

  /** Load all saved games. */
  loadAllGames(): SavedSnakesLaddersGame[] {
    try {
      const data = localStorage.getItem(this.STORAGE_KEY);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error('Failed to load snakes & ladders games:', error);
      return [];
    }
  }

  /** Delete a saved game by ID. */
  deleteGame(gameId: string): boolean {
    try {
      const games = this.loadAllGames();
      const filtered = games.filter((g) => g.gameId !== gameId);
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(filtered));
      return true;
    } catch (error) {
      console.error('Failed to delete snakes & ladders game:', error);
      return false;
    }
  }

  /** Clear all saved games. */
  clearAllGames(): boolean {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
      localStorage.removeItem(this.AUTO_SAVE_KEY);
      return true;
    } catch (error) {
      console.error('Failed to clear all snakes & ladders games:', error);
      return false;
    }
  }

  /** Sync saved games from cloud storage. */
  async syncFromCloud(): Promise<boolean> {
    try {
      if (!this.syncService) return false;
      console.warn('Cloud sync not yet implemented for snakes & ladders games');
      return false;
    } catch (error) {
      console.error('Failed to sync snakes & ladders games from cloud:', error);
      return false;
    }
  }

  /** Sync saved games to cloud storage. */
  async syncToCloud(): Promise<boolean> {
    try {
      if (!this.syncService) return false;
      console.warn('Cloud sync not yet implemented for snakes & ladders games');
      return true;
    } catch (error) {
      console.error('Failed to sync snakes & ladders games to cloud:', error);
      return false;
    }
  }
}
